import {
  addDays,
  addHours,
  differenceInCalendarDays,
  getHours,
  setHours,
  startOfDay,
} from 'date-fns';

import {clamp} from 'ramda';

import {BUSINESS_HOURS_COUNT, TEST_DRIVE_CALENDAR} from '../constants/TEST_DRIVE_CALENDAR';

export type TestDriveCalendarSlot = {
  start: Date;
  end: Date | null;
  isTimeIncluded: boolean;
  /** The column the pointer is over — the row's `+` hint sits on it. */
  columnIndex: number;
};

interface SlotFromPointerArgs {
  pointerX: number;
  rowLeft: number;
  rowWidth: number;
  windowStart: Date;
  rangeDays: number;
}

/** Keeps a click on the trailing edge inside the last column instead of rolling into the next day. */
const MAX_FRACTION = 0.999999;

/**
 * Inverse of `getTestDriveBarLayout`. The row has no per-slot DOM cells — its grid lines are a CSS
 * gradient — so the clicked slot is computed from the pointer against the row's own box.
 *
 * A 7-day row snaps to the hour column it was clicked in and opens a drive of the default length.
 * A 30-day column is one whole day: it yields the date only, and the time is left for the user.
 */
export const getSlotFromPointer = (args: SlotFromPointerArgs): TestDriveCalendarSlot => {
  const fraction =
    args.rowWidth > 0 ? clamp(0, MAX_FRACTION, (args.pointerX - args.rowLeft) / args.rowWidth) : 0;

  const dayOffset = fraction * args.rangeDays;
  const dayIndex = Math.floor(dayOffset);
  const day = startOfDay(addDays(args.windowStart, dayIndex));

  if (args.rangeDays !== TEST_DRIVE_CALENDAR.RANGE_DAYS.WEEK) {
    return {
      start: day,
      end: null,
      isTimeIncluded: false,
      columnIndex: dayIndex,
    };
  }

  const hourIndex = Math.floor((dayOffset - dayIndex) * BUSINESS_HOURS_COUNT);
  const start = setHours(day, TEST_DRIVE_CALENDAR.BUSINESS_START_HOUR + hourIndex);

  return {
    start,
    end: addHours(start, TEST_DRIVE_CALENDAR.OPEN_DRIVE_HOURS),
    isTimeIncluded: true,
    columnIndex: dayIndex * BUSINESS_HOURS_COUNT + hourIndex,
  };
};

/** Same columns as `getSlotFromPointer`; the segment start is already clamped to the window. */
export const getStartColumnIndex = (start: Date, firstDay: Date, isHourSubdivided: boolean) => {
  const dayIndex = differenceInCalendarDays(start, firstDay);

  if (!isHourSubdivided) {
    return dayIndex;
  }

  const hourIndex = clamp(
    0,
    BUSINESS_HOURS_COUNT - 1,
    getHours(start) - TEST_DRIVE_CALENDAR.BUSINESS_START_HOUR
  );

  return dayIndex * BUSINESS_HOURS_COUNT + hourIndex;
};
